import type { CatalogItemAssociationConfig } from '~/components/Accommodation/types/catalogItem';

import type { StructuredFacilityGroup } from '~/hooks/apis/offers/useGetOffer';

import * as AccommodationService from '~/services/AccommodationService';
import OffersService from '~/services/OffersService';
import { HttpResponse } from '~/services/types';

import { getHotelUniquePropertyIds } from '~/utils/hotelUtils';

export interface OfferStructuredFacilityRow extends AccommodationService.AdminFacility {
  featured: boolean | null;
  defaultFeatured: boolean;
  defaultDisplayOrder?: number;
}

interface OfferStructuredFacilityAddPayload {
  facilityCode: string;
  displayOrder?: number | null;
}

interface OfferStructuredFacilityUpdatePayload {
  displayOrder?: number | null;
}

const APPROVED_FACILITY_LIST_QUERY = {
  status: 'approved' as const,
  limit: '10000',
  page: '1',
};

function response<T>(result: T): HttpResponse<T> {
  return {
    status: 200,
    message: '',
    result,
  };
}

async function listApprovedFacilities() {
  const facilitiesResponse = await AccommodationService.listFacilities(APPROVED_FACILITY_LIST_QUERY);
  return facilitiesResponse.result.facilities;
}

function getOfferStructuredFacilities(offer: App.Offer): Array<StructuredFacilityGroup> {
  return offer.structured_facilities ?? [];
}

function getOfferReservationPropertyId(offer: App.Offer): string | undefined {
  const uniquePropertyIds = getHotelUniquePropertyIds(offer as App.AccommodationOffer);
  return uniquePropertyIds.length === 1 ? uniquePropertyIds[0] : undefined;
}

function getFacilityGroup(facility: AccommodationService.AdminFacility) {
  if (!facility.catalogGroup) {
    throw new Error(`Facility ${facility.code} is missing a catalog group`);
  }

  return {
    code: facility.catalogGroup.code,
    name: facility.catalogGroup.name,
    icon: facility.catalogGroup.icon ?? null,
    displayOrder: facility.catalogGroup.displayOrder ?? null,
  };
}

function sortStructuredFacilities(
  structuredFacilities: Array<StructuredFacilityGroup>,
  facilityByCode: Map<string, AccommodationService.AdminFacility>,
) {
  return structuredFacilities
    .filter((group) => group.facilities.length > 0)
    .map((group) => ({
      ...group,
      facilities: [...group.facilities].sort((first, second) => {
        const firstOrder =
          first.displayOrder ?? facilityByCode.get(first.code)?.displayOrder ?? Number.MAX_SAFE_INTEGER;
        const secondOrder =
          second.displayOrder ?? facilityByCode.get(second.code)?.displayOrder ?? Number.MAX_SAFE_INTEGER;

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return first.name.localeCompare(second.name);
      }),
    }))
    .sort((first, second) => {
      const firstOrder = first.group.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const secondOrder = second.group.displayOrder ?? Number.MAX_SAFE_INTEGER;

      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }

      return first.group.name.localeCompare(second.group.name);
    });
}

function mapStructuredFacilityGroupToCatalogGroup(
  group: StructuredFacilityGroup['group'],
  catalogGroup: AccommodationService.AdminCatalogGroup | undefined,
) {
  return {
    ...catalogGroup,
    id: catalogGroup?.id ?? group.code,
    code: group.code ?? catalogGroup?.code,
    type: catalogGroup?.type ?? 'facility',
    name: group.name ?? catalogGroup?.name,
    displayOrder: group.displayOrder ?? catalogGroup?.displayOrder ?? undefined,
    icon: group.icon ?? catalogGroup?.icon ?? null,
  } as AccommodationService.AdminCatalogGroup;
}

function flattenStructuredFacilities(
  structuredFacilities: Array<StructuredFacilityGroup>,
  facilityByCode: Map<string, AccommodationService.AdminFacility>,
): Array<OfferStructuredFacilityRow> {
  return structuredFacilities.flatMap((group) =>
    group.facilities.map((facility) => {
      const catalogFacility = facilityByCode.get(facility.code);

      return {
        code: facility.code,
        name: facility.name,
        description: catalogFacility?.description ?? null,
        aliases: catalogFacility?.aliases,
        catalogGroup: mapStructuredFacilityGroupToCatalogGroup(group.group, catalogFacility?.catalogGroup),
        icon: (facility.icon ?? catalogFacility?.icon ?? null) as AccommodationService.Icon | null,
        featured: null,
        defaultFeatured: Boolean(catalogFacility?.featured),
        displayOrder: facility.displayOrder,
        defaultDisplayOrder: catalogFacility?.displayOrder,
        status: 'approved',
      };
    }),
  );
}

async function getOfferAndFacilityMap(offerId: string) {
  const [offerResponse, facilities] = await Promise.all([OffersService.getOffer(offerId), listApprovedFacilities()]);
  const facilityByCode = new Map(facilities.map((facility) => [facility.code, facility]));

  return {
    offer: offerResponse.result as App.Offer,
    facilities,
    facilityByCode,
  };
}

async function updateOfferStructuredFacilities(
  offerId: string,
  buildNextFacilities: (
    structuredFacilities: Array<StructuredFacilityGroup>,
    facilityByCode: Map<string, AccommodationService.AdminFacility>,
  ) => Array<StructuredFacilityGroup>,
) {
  const { offer, facilityByCode } = await getOfferAndFacilityMap(offerId);
  const nextStructuredFacilities = sortStructuredFacilities(
    buildNextFacilities(getOfferStructuredFacilities(offer), facilityByCode),
    facilityByCode,
  );

  await OffersService.updateOffer({ structured_facilities: nextStructuredFacilities }, offerId);

  return response(flattenStructuredFacilities(nextStructuredFacilities, facilityByCode));
}

export function mapRowsToStructuredFacilities(rows: Array<OfferStructuredFacilityRow>): Array<StructuredFacilityGroup> {
  const structuredFacilitiesByGroupCode = new Map<string, StructuredFacilityGroup>();

  rows.forEach((row) => {
    if (!row.catalogGroup) {
      throw new Error(`Facility ${row.code} is missing a catalog group`);
    }

    const group = {
      code: row.catalogGroup.code,
      name: row.catalogGroup.name,
      icon: row.catalogGroup.icon ?? null,
      displayOrder: row.catalogGroup.displayOrder ?? null,
    };
    const structuredFacility = {
      code: row.code,
      name: row.name,
      icon: row.icon ?? null,
      displayOrder: row.displayOrder ?? null,
    };
    const existingGroup = structuredFacilitiesByGroupCode.get(group.code);

    if (existingGroup) {
      existingGroup.facilities.push(structuredFacility);
      return;
    }

    structuredFacilitiesByGroupCode.set(group.code, {
      group,
      facilities: [structuredFacility],
    });
  });

  return Array.from(structuredFacilitiesByGroupCode.values());
}

async function importOfferStructuredFacilities(offerId: string) {
  const { offer, facilityByCode } = await getOfferAndFacilityMap(offerId);
  const reservationPropertyId = getOfferReservationPropertyId(offer);

  if (!reservationPropertyId) {
    throw new Error('No single accommodation property is associated with this offer');
  }

  const propertyFacilitiesResponse = await AccommodationService.exportPropertyFacilities({
    propertyId: reservationPropertyId,
  });

  if (propertyFacilitiesResponse.result.structuredFacilities.length === 0) {
    throw new Error(propertyFacilitiesResponse.result.message || 'No facilities were found for this property');
  }

  const nextStructuredFacilities = sortStructuredFacilities(
    propertyFacilitiesResponse.result.structuredFacilities,
    facilityByCode,
  );

  return response({
    items: flattenStructuredFacilities(nextStructuredFacilities, facilityByCode),
  });
}

async function saveImportedOfferStructuredFacilities(offerId: string, rows: Array<OfferStructuredFacilityRow>) {
  const { facilityByCode } = await getOfferAndFacilityMap(offerId);
  const nextStructuredFacilities = sortStructuredFacilities(mapRowsToStructuredFacilities(rows), facilityByCode);

  await OffersService.updateOffer({ structured_facilities: nextStructuredFacilities }, offerId);

  return response({
    items: flattenStructuredFacilities(nextStructuredFacilities, facilityByCode),
  });
}

export const offerStructuredFacilityAssociationConfig: CatalogItemAssociationConfig<
  AccommodationService.AdminFacility,
  OfferStructuredFacilityRow,
  OfferStructuredFacilityAddPayload,
  OfferStructuredFacilityUpdatePayload
> = {
  listAllItems: async (params) => {
    const facilitiesResponse = await AccommodationService.listFacilities(params);
    return response({
      items: facilitiesResponse.result.facilities,
    });
  },
  listAssociatedItems: async (pathParams, queryParams) => {
    const { offer, facilityByCode } = await getOfferAndFacilityMap(pathParams.offerId);
    const allRows = flattenStructuredFacilities(getOfferStructuredFacilities(offer), facilityByCode);
    const filteredRows =
      queryParams.status === 'draft'
        ? []
        : allRows.filter((row) => {
            const matchesName = queryParams.name
              ? row.name.toLowerCase().includes(queryParams.name.toLowerCase())
              : true;
            const matchesGroup = queryParams.catalogGroupId
              ? row.catalogGroup?.id === queryParams.catalogGroupId
              : true;

            return matchesName && matchesGroup;
          });
    const page = Number(queryParams.page);
    const limit = Number(queryParams.limit);
    const startIndex = (page - 1) * limit;

    return response({
      items: filteredRows.slice(startIndex, startIndex + limit),
      total: filteredRows.length,
    });
  },
  getAllAssociatedItemCodes: async (pathParams) => {
    const { offer, facilityByCode } = await getOfferAndFacilityMap(pathParams.offerId);

    return response({
      items: flattenStructuredFacilities(getOfferStructuredFacilities(offer), facilityByCode),
    });
  },
  addItem: async (pathParams, data) => {
    return updateOfferStructuredFacilities(pathParams.offerId, (structuredFacilities, facilityByCode) => {
      const facility = facilityByCode.get(data.facilityCode);

      if (!facility) {
        throw new Error(`Facility ${data.facilityCode} was not found`);
      }

      if (structuredFacilities.some((group) => group.facilities.some((item) => item.code === data.facilityCode))) {
        throw new Error(`Facility ${data.facilityCode} is already associated with this offer`);
      }

      const group = getFacilityGroup(facility);
      const existingGroup = structuredFacilities.find((item) => item.group.code === group.code);
      const newFacility = {
        code: facility.code,
        name: facility.name,
        icon: facility.icon ?? null,
        displayOrder: data.displayOrder ?? null,
      };

      if (existingGroup) {
        return structuredFacilities.map((item) =>
          item.group.code === group.code ? { ...item, facilities: [...item.facilities, newFacility] } : item,
        );
      }

      return [...structuredFacilities, { group, facilities: [newFacility] }];
    }).then((rows) => response(rows.result.find((item) => item.code === data.facilityCode)));
  },
  updateItem: async (pathParams, data) => {
    return updateOfferStructuredFacilities(pathParams.offerId, (structuredFacilities) =>
      structuredFacilities.map((group) => ({
        ...group,
        facilities: group.facilities.map((facility) =>
          facility.code === pathParams.facilityCode
            ? { ...facility, displayOrder: data.displayOrder ?? null }
            : facility,
        ),
      })),
    ).then((rows) => response(rows.result.find((item) => item.code === pathParams.facilityCode)));
  },
  removeItem: async (pathParams) => {
    await updateOfferStructuredFacilities(pathParams.offerId, (structuredFacilities) =>
      structuredFacilities
        .map((group) => ({
          ...group,
          facilities: group.facilities.filter((facility) => facility.code !== pathParams.facilityCode),
        }))
        .filter((group) => group.facilities.length > 0),
    );

    return response(undefined);
  },
  importItems: async (pathParams) => {
    return importOfferStructuredFacilities(pathParams.offerId);
  },
  saveImportedItems: async (pathParams, items) => {
    return saveImportedOfferStructuredFacilities(pathParams.offerId, items);
  },

  allItemsQueryKey: ['accommodation', 'facilities', 'all'],
  associatedItemsQueryKey: (offerId) => ['offers', 'structured-facilities', offerId],
  allAssociatedCodesQueryKey: (offerId) => ['offers', 'structured-facilities', offerId, 'all-codes'],

  itemName: 'Facility',
  itemNamePlural: 'Facilities',
  associationContext: 'offer',
  showFeatured: false,
};
